import React from 'react';
import { Star, ArrowRight, Plus } from 'lucide-react';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export const TestimonialsSection = ({ theme, t, language, scrollToConfigurator }) => {
  const testimonials = [
    { 
      text: language === 'de' 
        ? 'Als Office Managerin habe ich schon viel probiert – nichts kam so gut an wie die Freitags-Donuts. Die Kollegen freuen sich jede Woche darauf!' 
        : 'As an office manager, I\'ve tried a lot – nothing was as popular as Friday donuts. Colleagues look forward to it every week!', 
      name: 'Sarah M.', 
      role: language === 'de' ? 'Office Managerin' : 'Office Manager', 
      company: 'TechStart GmbH' 
    },
    { 
      text: language === 'de' 
        ? 'Perfekt für unsere Team-Meetings! Die Qualität ist hervorragend und die flexible Lieferung passt sich unserem Kalender an.' 
        : 'Perfect for our team meetings! The quality is excellent and the flexible delivery adapts to our calendar.', 
      name: 'Michael K.', 
      role: 'HR Manager', 
      company: 'Kreativ Agentur' 
    },
    { 
      text: language === 'de' 
        ? 'Seit dem Donut-Abo haben wir messbar bessere Stimmung im Büro. Die Investition lohnt sich – auch als kleines Dankeschön an alle.' 
        : 'Since the donut subscription, we have measurably better mood in the office. The investment is worth it – also as a small thank you to everyone.', 
      name: 'Lisa W.', 
      role: language === 'de' ? 'Geschäftsführerin' : 'CEO', 
      company: 'Finance Solutions' 
    },
  ];

  return (
    <section id="testimonials" className="py-24 relative overflow-hidden" style={{ backgroundColor: theme.bg }}>
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="text-sm font-bold" style={{ color: BRAND.turquoise }}>
            {t.testimonials.title}
          </span>
          <h2 className="text-3xl md:text-4xl font-black mt-2">
            {language === 'de' ? 'Das sagen Office Manager & HR-Verantwortliche' : 'What Office Managers & HR Professionals Say'}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, i) => (
            <div 
              key={i} 
              className="p-8 rounded-3xl" 
              style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 fill-current" style={{ color: BRAND.yellow }} />
                ))}
              </div>
              <p className="text-base mb-8 leading-relaxed" style={{ color: theme.textMuted }}>
                "{testimonial.text}"
              </p>
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white" 
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                >
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold">{testimonial.name}</div>
                  <div className="text-sm" style={{ color: theme.textMuted }}>
                    {testimonial.role}, {testimonial.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* CTA */}
        <div className="text-center mt-12">
          <button 
            onClick={() => scrollToConfigurator()}
            className="group px-10 py-4 rounded-full font-bold text-lg text-white transition-all hover:scale-105" 
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
          >
            Auch für dein Team bestellen 
            <ArrowRight className="inline w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const FAQSection = ({ theme, t, openFaq, setOpenFaq, faqRef }) => {
  const faqItems = [
    { q: t.faq.q1, a: t.faq.a1 },
    { q: t.faq.q2, a: t.faq.a2 },
    { q: t.faq.q3, a: t.faq.a3 },
    { q: t.faq.q4, a: t.faq.a4 },
    { q: t.faq.q5, a: t.faq.a5 },
    { q: t.faq.q6, a: t.faq.a6 },
  ];

  return (
    <section 
      data-section="faq" 
      ref={faqRef} 
      className="py-24 relative overflow-hidden" 
      style={{ backgroundColor: theme.bgSecondary }}
    >
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black">{t.faq.title}</h2>
        </div>
        
        <div className="space-y-6">
          {faqItems.map((item, i) => (
            <div 
              key={i} 
              className="rounded-2xl overflow-hidden transition-all duration-300" 
              style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}
            >
              <button 
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full p-5 flex items-center justify-between text-left transition-colors"
                style={{ backgroundColor: openFaq === i ? `${BRAND.turquoise}08` : 'transparent' }}
              >
                <h4 className="font-bold text-base pr-4">{item.q}</h4>
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
                  style={{ 
                    backgroundColor: openFaq === i ? `${BRAND.turquoise}20` : `${theme.border}`,
                    transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)'
                  }}
                >
                  <Plus className="w-4 h-4" style={{ color: openFaq === i ? BRAND.turquoise : theme.textMuted }} />
                </div>
              </button>
              <div 
                className="overflow-hidden transition-all duration-300"
                style={{ 
                  maxHeight: openFaq === i ? '200px' : '0px',
                  opacity: openFaq === i ? 1 : 0
                }}
              >
                <p className="px-5 pb-5 text-sm" style={{ color: theme.textMuted }}>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCTASection = ({ theme, t, scrollToConfigurator }) => {
  return (
    <section 
      className="py-24 relative overflow-hidden" 
      style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}15, ${BRAND.pink}15)` }}
    >
      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <h2 className="text-3xl md:text-5xl font-black mb-6">
          {t.cta.title}
        </h2>
        <p className="text-xl mb-10" style={{ color: theme.textMuted }}>
          {t.cta.subtitle}
        </p>
        <button 
          onClick={() => scrollToConfigurator()}
          className="group px-12 py-5 rounded-full font-bold text-xl text-white transition-all hover:scale-105 shadow-xl" 
          style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
        >
          {t.cta.button} 
          <ArrowRight className="inline w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform" />
        </button>
        <p className="mt-6 text-sm" style={{ color: theme.textMuted }}>
          {t.cta.benefits}
        </p>
      </div>
    </section>
  );
};

export default TestimonialsSection;
